import { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';

let deferredPrompt = null;

// Capture the beforeinstallprompt event globally
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
  });
}

export function PwaInstallBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [showIosGuide, setShowIosGuide] = useState(false);

  useEffect(() => {
    // Don't show if already installed as PWA
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    // Don't show if dismissed recently
    const dismissed = localStorage.getItem('pwa_banner_dismissed');
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      if (Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000) return; // 7 days
    }

    const ua = navigator.userAgent || '';
    const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIos(ios);

    // Show on mobile only
    if (window.innerWidth <= 768) {
      // For Android: show if deferredPrompt available or after a small delay
      if (deferredPrompt || ios) {
        setShowBanner(true);
      } else {
        // Wait for beforeinstallprompt
        const handler = (e) => {
          e.preventDefault();
          deferredPrompt = e;
          setShowBanner(true);
        };
        window.addEventListener('beforeinstallprompt', handler);
        // Also show for iOS after brief delay
        const timer = setTimeout(() => {
          if (ios) setShowBanner(true);
        }, 2000);
        return () => {
          window.removeEventListener('beforeinstallprompt', handler);
          clearTimeout(timer);
        };
      }
    }
  }, []);

  const handleInstall = async () => {
    if (isIos) {
      setShowIosGuide(true);
      return;
    }
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') {
        setShowBanner(false);
      }
      deferredPrompt = null;
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa_banner_dismissed', Date.now().toString());
  };

  if (!showBanner) return null;

  if (showIosGuide) {
    return (
      <div className="bg-white border border-sky-200 rounded-xl mx-4 mb-3 p-4 shadow-sm" data-testid="pwa-ios-guide">
        <div className="flex items-start justify-between mb-2">
          <p className="font-semibold text-slate-800 text-sm">Come installare su iPhone/iPad</p>
          <button onClick={() => { setShowIosGuide(false); handleDismiss(); }} className="text-slate-400 hover:text-slate-600 p-0.5">
            <X className="w-4 h-4" />
          </button>
        </div>
        <ol className="text-xs text-slate-600 space-y-1.5 list-decimal list-inside">
          <li>Tocca l'icona <strong>Condividi</strong> (il quadrato con la freccia in su) nella barra di Safari</li>
          <li>Scorri e seleziona <strong>"Aggiungi a schermata Home"</strong></li>
          <li>Conferma toccando <strong>"Aggiungi"</strong></li>
        </ol>
        <p className="text-xs text-sky-600 mt-2 font-medium">L'app apparira' sulla schermata Home come un'app nativa!</p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-sky-50 to-emerald-50 border border-sky-200 rounded-xl mx-4 mb-3 p-3 shadow-sm" data-testid="pwa-install-banner">
      <div className="flex items-center gap-3">
        <div className="bg-sky-500 rounded-lg p-2 flex-shrink-0">
          <Download className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-800 text-sm">Installa l'App</p>
          <p className="text-xs text-slate-500 truncate">Accesso rapido dal tuo telefono</p>
        </div>
        <button
          onClick={handleInstall}
          className="bg-sky-500 hover:bg-sky-600 text-white text-xs font-semibold px-3 py-1.5 rounded-lg flex-shrink-0 transition-colors"
          data-testid="pwa-install-btn"
        >
          Installa
        </button>
        <button onClick={handleDismiss} className="text-slate-400 hover:text-slate-600 p-0.5 flex-shrink-0">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
